import React, { useRef } from 'react';
import { Clock, Calendar } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

export const BusinessHours = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-10 sm:py-14 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              <span className="gradient-text-blue">Business Hours</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              We're here when you need us. Visit us during our operating hours.
            </p>
          </div>

          <div className="metallic-card p-8 sm:p-12 rounded-2xl">
            <div className="flex items-center justify-center mb-8">
              <div className="p-4 rounded-full bg-primary/10 animate-pulse-slow">
                <Clock className="h-12 w-12 text-primary" />
              </div>
            </div>

            <div className="space-y-6">
              {/* Monday to Saturday */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="p-6 bg-background/50 rounded-xl border border-border"
              >
                <div className="flex flex-col items-center justify-center gap-4 text-center">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="font-semibold text-lg">Monday - Saturday</span>
                  </div>
                  <span className="text-2xl font-bold text-primary">9:00 AM - 9:00 PM</span>
                </div>
              </motion.div>

              {/* Sunday */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="p-6 bg-background/50 rounded-xl border border-border"
              >
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="font-semibold text-lg">Sunday</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-accent/5 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Morning</p>
                    <p className="text-xl font-bold text-accent">10:00 AM - 2:00 PM</p>
                  </div>
                  <div className="text-center p-3 bg-accent/5 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-1">Evening</p>
                    <p className="text-xl font-bold text-accent">4:00 PM - 10:00 PM</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center text-muted-foreground mt-8"
            >
              Need to reach us outside business hours? Contact us via WhatsApp for immediate assistance.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
